import type { IterableEmbeddedButton, IterableEmbeddedMessage } from '@iterable/web-sdk'
import { HOME_DELIVERY_GUIDE_PAGE_PATH } from 'utils/constants'

/**
 * Everything in an `IterableEmbeddedMessage` below `metadata` is optional and authored in a
 * marketing tool, so it is untrusted input: a campaign can ship with no title, no buttons, a
 * button with no label, or an action with no data. These guards are what let the renderer stay
 * free of `?.` chains and of assertions, per the repo's TypeScript rules.
 */

/**
 * A message with copy worth drawing.
 *
 * Copy specifically, not "anything at all": the banner is built from a title and/or a description,
 * so a campaign carrying only buttons has nothing to announce and no shape to render in. Those are
 * dropped before they reach the placement rather than drawn as a bare row of buttons.
 */
export type RenderableMessage = IterableEmbeddedMessage & {
  elements: NonNullable<IterableEmbeddedMessage['elements']>
}

export function isRenderable(message: IterableEmbeddedMessage): message is RenderableMessage {
  const elements = message.elements
  if (!elements) return false
  return Boolean(elements.title?.trim() || elements.body?.trim())
}

/** A button we can actually draw: it has a label. A label-less button is dropped, not rendered blank. */
export type LabelledButton = IterableEmbeddedButton & { title: string }

export function isLabelledButton(button: IterableEmbeddedButton): button is LabelledButton {
  return typeof button.title === 'string' && button.title.trim().length > 0
}

export function labelledButtonsOf(message: RenderableMessage): LabelledButton[] {
  return (message.elements.buttons ?? []).filter(isLabelledButton)
}

/**
 * Iterable encodes the two action types asymmetrically: an **Open URL** action is
 * `{ type: 'openUrl', data: '<url>' }`, while a **custom action** puts the whole `action://<id>`
 * string in `type` and leaves `data` empty. `targetUrlOf` flattens that back into the single
 * string `IterableEmbeddedManager#click` expects, which is the same thing the SDK's own
 * (unexported) `getTargetUrl` does.
 */
export const OPEN_URL = 'openUrl'

export function targetUrlOf(action: { type: string; data?: string } | undefined): string {
  if (!action) return ''
  return action.type === OPEN_URL ? (action.data ?? '') : action.type
}

/** The `<id>` of an `action://<id>`, or `null` for anything else (including Open URL). */
export function customActionIdOf(action: { type: string; data?: string } | undefined): string | null {
  if (!action || action.type === OPEN_URL) return null
  const withoutScheme = action.type.replace(/^(action|itbl):\/\//, '')
  return withoutScheme === action.type ? null : withoutScheme
}

/**
 * Where a clicked target goes. The one definition both the button (to mark a link that leaves the
 * app) and the click handler in `actions.ts` (to follow it) read, so the two cannot disagree.
 *
 * - `custom`: an `action://` or `itbl://` target. The SDK hands these to the custom-action handler
 *   before any URL handling, matching the prefix case-sensitively, as here.
 * - `in-app`: same origin; routed, so a provider mid-visit keeps their state and their Daily call.
 *   `newTab` marks the pages in `IN_APP_NEW_TAB_PATHS`, which open in a tab of their own instead.
 * - `external`: another origin, over `http:` or `https:` only; opened in a new tab.
 * - `refused`: empty, unparseable, or any other scheme (`javascript:`, `data:`, `mailto:`, an
 *   uppercase `ACTION://`…). Closed by default, like everything else a campaign can author.
 */
export type Destination =
  | { kind: 'custom' }
  | { kind: 'in-app'; pathname: string; path: string; newTab: boolean }
  | { kind: 'external'; href: string }
  | { kind: 'refused'; reason: 'empty' | 'unparseable' | 'scheme' }

const CUSTOM_ACTION_PREFIXES = ['action://', 'itbl://'] as const
const EXTERNAL_PROTOCOLS: readonly string[] = ['http:', 'https:']

/**
 * In-app pages an announcement opens in a new tab: reference pages a provider reads beside the app,
 * not a place to go. The Home Delivery guide is one, as it is from the visit's Requested pharmacy
 * card, so the dashboard stays where it was. Matched on the bare path, so a `?section=` still counts.
 */
const IN_APP_NEW_TAB_PATHS: readonly string[] = [HOME_DELIVERY_GUIDE_PAGE_PATH]

export function destinationOf(target: string): Destination {
  if (target.trim() === '') return { kind: 'refused', reason: 'empty' }
  if (CUSTOM_ACTION_PREFIXES.some((prefix) => target.startsWith(prefix))) return { kind: 'custom' }

  let url: URL
  try {
    url = new URL(target, window.location.origin)
  } catch {
    return { kind: 'refused', reason: 'unparseable' }
  }

  if (!EXTERNAL_PROTOCOLS.includes(url.protocol)) return { kind: 'refused', reason: 'scheme' }
  if (url.origin === window.location.origin) {
    return {
      kind: 'in-app',
      pathname: url.pathname,
      path: `${url.pathname}${url.search}${url.hash}`,
      newTab: IN_APP_NEW_TAB_PATHS.includes(url.pathname),
    }
  }
  return { kind: 'external', href: url.href }
}

/** Whether a button's action opens a new tab, so it can say so before it is clicked. */
export function opensInNewTab(action: { type: string; data?: string } | undefined): boolean {
  const destination = destinationOf(targetUrlOf(action))
  return destination.kind === 'external' || (destination.kind === 'in-app' && destination.newTab)
}

/**
 * The leading icons a campaign may ask for, by key, in its custom payload (`{"icon":"truck-fast"}`).
 *
 * A closed list rather than a lookup by name: the payload is authored in Iterable, and an open
 * lookup would let a campaign pull any icon in the kit onto a clinician's dashboard, or none at all
 * on a typo. Anything not listed here, including no payload, draws the bell.
 */
export const ANNOUNCEMENT_ICONS = ['bell', 'truck-fast'] as const
export type AnnouncementIcon = (typeof ANNOUNCEMENT_ICONS)[number]

export function announcementIconOf(message: IterableEmbeddedMessage): AnnouncementIcon {
  const requested: unknown = message.payload?.['icon']
  return ANNOUNCEMENT_ICONS.find((icon) => icon === requested) ?? 'bell'
}
